import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

import { useObservability } from 'platform/observability';
import type { ErrorResponse } from 'react-router';
import { isRouteErrorResponse, useAsyncError, useRouteError } from 'react-router';
import {
  ErrorPageContent,
  useTrackGenericErrorPageViewEvent,
} from 'shared/routes/errors/GenericErrorPage';
import { NotFoundPage } from 'shared/routes/errors/NotFoundPage';

import { CustomerBoundaryUI } from './CustomerBoundary';
import {
  SystemError,
  TeamError,
  UnauthenticatedError,
  UnauthorizedError,
  UserError,
} from './Error';

/**
 * This component is responsible for tracking the error in observability and rendering the appropriate error page.
 * NOTE: This component should ONLY be used as a top-level error boundary in the application.
 * @returns The error page to render.
 */
export const ErrorHandler = () => {
  const asyncError = useAsyncError();
  const routeError = useRouteError();
  const error = useTrackCaughtError(asyncError ?? routeError);
  return <ErrorUI error={error} />;
};

const ErrorUI: FC<{ error: unknown }> = ({ error }) => {
  if (isRouteErrorResponse(error)) {
    if (error?.status === 404) {
      return <NotFoundPage />;
    }
  }
  if (error instanceof UnauthenticatedError) {
    return <CustomerBoundaryUI authenticationError={error} />;
  }
  if (error instanceof UnauthorizedError) {
    return <CustomerBoundaryUI authorizationError={error} />;
  }
  return <ErrorPageContent />;
};

const getErrorData = (error: ErrorResponse | TeamError | Error): Record<string, unknown> => {
  const data: Record<string, unknown> = {
    // oxlint-disable-next-line typescript/no-misused-spread -- Collects the concrete subclass's own enumerable fields; Error declares `message`/`stack` non-enumerable, hence the explicit copy below.
    ...error,
    ...(error instanceof TeamError ? error.data : undefined),
    ...(error instanceof Error ? { message: error.message, stack: error.stack } : undefined),
  };

  // `TeamError` always defines `team`, and observability merges event data over contextual data,
  // so leaving the key present with no value would erase the team an enclosing ODTeamBoundary set.
  if (data.team === undefined) {
    delete data.team;
  }

  return data;
};

const useTrackCaughtError = (error: unknown) => {
  const routeError = isRouteErrorResponse(error) ? error : undefined;
  const { trackError } = useObservability();
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  /**
   * Tracks the error in observability with team data.
   */
  const trackBoundaryError = useCallback(
    (reason: string, boundaryError: ErrorResponse | TeamError | Error) => {
      const errorData = getErrorData(boundaryError);

      trackGenericErrorPageView(reason, 'system-error', errorData);

      // eslint-disable-next-line @opendoor/track-error-prefer-throw -- ErrorHandler is the terminal boundary reporter; rethrowing here would recurse.
      trackError(
        reason,
        boundaryError instanceof Error ? boundaryError : new Error(reason),
        errorData,
      );
    },
    [trackError, trackGenericErrorPageView],
  );
  useEffect(() => {
    if (routeError) {
      trackBoundaryError(
        '[RouteError] ' + `${routeError.status} - ${routeError.statusText}`,
        routeError,
      );
    } else if (error instanceof TeamError) {
      trackBoundaryError('[TeamError] ' + error.message, error);
    } else if (error instanceof SystemError) {
      trackBoundaryError('[SystemError] ' + error.message, error);
    } else if (error instanceof UserError) {
      trackBoundaryError('[UserError] ' + error.message, error);
    } else if (error instanceof Error) {
      trackBoundaryError('[Error] ' + error.message, error);
    } else if (error instanceof UnauthenticatedError || error instanceof UnauthorizedError) {
      // Auth errors do not trigger an `error-page-view`
      const errorType =
        error instanceof UnauthenticatedError ? '[UnauthenticatedError] ' : '[UnauthorizedError] ';
      const reason = errorType + error.message;

      // eslint-disable-next-line @opendoor/track-error-prefer-throw -- Auth errors are already caught by this boundary; this reports the terminal state.
      trackError(reason, error instanceof Error ? error : new Error(reason), {
        ...getErrorData(error),
      });
    } else if (error instanceof Response) {
      trackBoundaryError(
        '[ResponseThrown] ' + `${error.status} - ${error.statusText ?? 'Failed to redirect'}`,
        new Error('failed-to-redirect'),
      );
    } else {
      trackBoundaryError(
        '[UnknownError]' + String(error),
        new TeamError(new Error(String(error)), 'system-error'),
      );
    }
  }, [error, routeError, trackBoundaryError, trackError]);
  return routeError ?? error;
};
